import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MarketingShell, Seo, breadcrumbLd } from '@/seo/kit';

const SUBTLE = '#5B6678';
const NAVY = '#13294B';
const HERO_BG = '#EEF2F8';
const GRAY = '#F2F4F7';
const BLUE = '#1E4ED8';

const h1Style = { color: NAVY, fontWeight: 800, letterSpacing: '-0.02em' };
const h2Style = { color: NAVY, fontWeight: 700 };
const BODY = 'text-[17px] md:text-[18px] leading-relaxed';

function Container({ children, className = '' }) {
  return <div className={`max-w-7xl mx-auto px-5 md:px-8 ${className}`}>{children}</div>;
}

// Editorial asymmetric row: heading left, body right — fills the full width.
function Row({ title, children, bg }) {
  return (
    <section style={bg ? { background: bg } : undefined}>
      <Container className="py-14 md:py-20">
        <div className="grid md:grid-cols-3 gap-6 md:gap-12">
          <h2 className="text-2xl md:text-3xl" style={h2Style}>{title}</h2>
          <div className="md:col-span-2">{children}</div>
        </div>
      </Container>
    </section>
  );
}

export default function About() {
  const navigate = useNavigate();
  return (
    <MarketingShell>
      <Seo
        title="About DrawReady | Construction draw management for builders and lenders"
        description="DrawReady is built by people who have run construction projects and managed draws — so it works the way the money really moves, from the jobsite to the lender."
        path="/about"
        jsonLd={breadcrumbLd([{ label: 'Home', href: '/' }, { label: 'About', href: '/about' }])}
      />

      {/* Hero — full-bleed */}
      <section style={{ background: HERO_BG }}>
        <Container className="py-20 md:py-28">
          <h1 className="text-5xl md:text-6xl lg:text-7xl" style={h1Style}>About DrawReady</h1>
          <p className={`${BODY} mt-6 max-w-3xl`} style={{ color: SUBTLE }}>
            DrawReady is construction draw management for builders and lenders. It helps you track every dollar, document and milestone as you build — then turn it into a bank-ready draw without rebuilding the story for your lender.
          </p>
        </Container>
      </section>

      {/* Alternating full-bleed editorial rows */}
      <Row title="Why we built it">
        <p className={BODY} style={{ color: SUBTLE }}>
          A single missing document can stall a draw — and a stalled draw stalls a project and a funding. We lived that on real projects, on both sides of the table: as the builder assembling the package, and as the party reviewing it. DrawReady is the tool we wished we had.
        </p>
      </Row>

      <Row title="Who it's for" bg={GRAY}>
        <p className={BODY} style={{ color: SUBTLE }}>
          Builders and developers who want to know a draw is complete before they submit it, and the banks and lenders who receive those draws. One system keeps every project's draws, documents and budgets organized the same way, every time.
        </p>
      </Row>

      <Row title="How we build">
        <p className={BODY} style={{ color: SUBTLE }}>
          DrawReady is built from real construction and development experience — not a generic software template. We build for how construction actually gets funded, from the jobsite to the lender's inbox.
        </p>
      </Row>

      {/* Closing CTA — full-bleed blue */}
      <section style={{ background: BLUE }}>
        <Container className="py-16 md:py-20">
          <h2 className="text-3xl md:text-4xl text-white" style={{ fontWeight: 700 }}>Have a draw coming up? Build it in DrawReady first.</h2>
          <button
            onClick={() => navigate('/signup')}
            className="mt-7 inline-block rounded-md font-semibold px-6 py-3 transition hover:opacity-90"
            style={{ background: '#fff', color: BLUE }}
            data-testid="about-cta"
          >
            Start your 30-day free trial
          </button>
        </Container>
      </section>
    </MarketingShell>
  );
}
